import type { ImageModel } from '@rocco/types/image-model';
import { Image } from '@rocco/components/image';
import { cn } from '@rocco/utils/cn';
import { getLogoBadgeBorderColor } from './logo-badge-border';

interface ProfessionalTileLogoProps {
    image: ImageModel;
    background: string | null;
    /** Render a smaller logo (for compact tile contexts like recently viewed). */
    compact?: boolean;
    /**
     * Override the badge size (e.g. a responsive `size-*` for larger avatar
     * contexts). Defaults to `size-22` (or `size-14` when `compact`).
     */
    className?: string;
    /**
     * Add a hairline border that contrasts with the badge fill so a white logo
     * on a white background (or a black logo on a dark one) doesn't blend in.
     * Opt-in so existing tile contexts (over imagery) are unaffected.
     */
    contrastBorder?: boolean;
}

export const ProfessionalTileLogo = (props: ProfessionalTileLogoProps) => {
    const {
        image,
        background,
        compact = false,
        className,
        contrastBorder = false,
    } = props;

    const borderColor = contrastBorder
        ? getLogoBadgeBorderColor(background)
        : undefined;

    return (
        <div
            className={cn(
                'flex items-center justify-center overflow-hidden rounded-full p-0.5',
                className ?? (compact ? 'size-14' : 'size-22'),
            )}
            style={{
                backgroundColor: background || 'white',
                ...(borderColor ? { border: `1px solid ${borderColor}` } : {}),
            }}
        >
            <Image
                src={image.src}
                alt={image.alt ?? 'Logo'}
                size="professionalLogo"
            />
        </div>
    );
};
